import { z } from "zod";

import { DatetimeColumnDisplayFormatTypeLiteral } from "../datetimeDisplayFormatTypes.js";
import { NumberDisplayFormatTypeLiteral } from "../numberDisplayFormatTypes.js";
import { getNormalEnum } from "../pseudoEnums.js";

import { DisplayTableColumnOutputType } from "./outputTypes.js";

//#region boolean columns
export const BooleanColumnDisplayFormatType = z.enum(["PLAIN"]);
export const BooleanColumnDisplayFormat = z.object({
  columnType: z.literal(DisplayTableColumnOutputType.BOOLEAN),
  format: BooleanColumnDisplayFormatType,
});
export type BooleanColumnDisplayFormat = z.infer<
  typeof BooleanColumnDisplayFormat
>;
//#endregion

//#region datetime columns
/** @WARN Keep in sync with packages/server/src/agents/logic-view/utils/metricCellHelpers.ts */
const DateColumnDisplayFormatTypeLiteral = z.enum([
  "PLAIN",
  /** A localized date representation with no time info */
  "DATE",
  /** A longer localized date representation with no time info */
  "DATE_LONG",
  /** A localized year and month representation with no day info (e.g. "January 2025") */
  "YEAR_MONTH",
  /** A localized year representation with no month info (e.g. "2025") */
  "YEAR",
]);
export type DateColumnDisplayFormatType = z.infer<
  typeof DateColumnDisplayFormatTypeLiteral
>;
export const DateColumnDisplayFormatType = getNormalEnum(
  DateColumnDisplayFormatTypeLiteral,
);

export const DateColumnDisplayFormat = z.object({
  columnType: z.literal(DisplayTableColumnOutputType.DATE),
  format: DateColumnDisplayFormatTypeLiteral,
});
export type DateColumnDisplayFormat = z.infer<typeof DateColumnDisplayFormat>;
//#endregion

//#region datetime columns

/** @WARN Keep in sync with packages/server/src/agents/logic-view/utils/metricCellHelpers.ts */
const DatetimeColumnDisplayFormatTimezoneLiteral = z.enum([
  /** Display the date/time as is, ignoring the presence of any offset */
  "IGNORE",
  /**
   * Convert the underlying datetime into the current viewer's browser's timezone
   * before displaying the date/time. If the underlying datetime has no offset,
   * it will be assumed to be in UTC prior to conversion.
   */
  "LOCAL",
]);
export type DatetimeColumnDisplayFormatTimezone = z.infer<
  typeof DatetimeColumnDisplayFormatTimezoneLiteral
>;
export const DatetimeColumnDisplayFormatTimezone = getNormalEnum(
  DatetimeColumnDisplayFormatTimezoneLiteral,
);

/** @WARN Keep in sync with packages/server/src/agents/logic-view/utils/metricCellHelpers.ts */
export const DatetimeColumnDisplayFormat = z.object({
  columnType: z.literal(DisplayTableColumnOutputType.DATETIME),
  format: DatetimeColumnDisplayFormatTypeLiteral,
  // Used by all formats except `PLAIN`
  timezone: DatetimeColumnDisplayFormatTimezoneLiteral.optional(),
});
export type DatetimeColumnDisplayFormat = z.infer<
  typeof DatetimeColumnDisplayFormat
>;
//#endregion

//#region number columns
export type NumberColumnDisplayFormatType = z.infer<
  typeof NumberDisplayFormatTypeLiteral
>;
export const NumberColumnDisplayFormatType = getNormalEnum(
  NumberDisplayFormatTypeLiteral,
);

/** @WARN Keep in sync with packages/server/src/agents/logic-view/utils/metricCellHelpers.ts */
export const NumberColumnDisplayFormat = z.object({
  columnType: z.literal(DisplayTableColumnOutputType.NUMBER),
  format: NumberDisplayFormatTypeLiteral,
  // Used by all formats except `PLAIN`
  numDecimalDigits: z.number(),
  // Used by `CURRENCY`, `CURRENCY_LONG`, and `FINANCIAL`
  currency: z.string(),
  // when numbers are big (1,000,000), abbreviate (1M)
  abbreviateLargeNumbers: z.boolean().optional(),
  // what string to use for NaN/null/empty
  nanFormat: z.string().optional(),
  // show separators (usually commas) between 000s
  showSeparators: z.boolean().optional(),
});
export type NumberColumnDisplayFormat = z.infer<
  typeof NumberColumnDisplayFormat
>;
//#endregion

//#region string columns
export const StringColumnDisplayFormatTypeLiteral = z.enum(["PLAIN", "URL"]);
export type StringColumnDisplayFormatType = z.infer<
  typeof StringColumnDisplayFormatTypeLiteral
>;
export const StringColumnDisplayFormatType = getNormalEnum(
  StringColumnDisplayFormatTypeLiteral,
);

export const StringColumnDisplayFormat = z.object({
  columnType: z.literal(DisplayTableColumnOutputType.STRING),
  format: StringColumnDisplayFormatTypeLiteral,
});
export type StringColumnDisplayFormat = z.infer<
  typeof StringColumnDisplayFormat
>;
//#endregion

//#region unknown columns
export const UnknownColumnDisplayFormatType = z.enum(["PLAIN"]);
export const UnknownColumnDisplayFormat = z.object({
  columnType: z.literal(DisplayTableColumnOutputType.UNKNOWN),
  format: UnknownColumnDisplayFormatType,
});
export type UnknownColumnDisplayFormat = z.infer<
  typeof UnknownColumnDisplayFormat
>;
//#endregion

export const ColumnDisplayFormat = z.union([
  BooleanColumnDisplayFormat,
  DateColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
  StringColumnDisplayFormat,
  UnknownColumnDisplayFormat,
]);
export type ColumnDisplayFormat = z.infer<typeof ColumnDisplayFormat>;
